/**
 * Client-side search + filters over the in-view category (feature #5).
 *
 * SHEET-SPECIFIC (v2): filters are built from the ACTIVE sheet's own columns
 * (product.attrs), so switching tabs switches the whole filter set — no more
 * columns/values leaking in from another sheet. Columns with too many distinct
 * values (free-text like model names) or only one value are skipped.
 */
import type { Product } from '@mitang/shared';

export interface ProductFilters {
  query: string;
  /** column name → selected value (only the active sheet's columns). */
  attrs: Record<string, string>;
}

export const emptyFilters: ProductFilters = { query: '', attrs: {} };

export function hasActiveFilters(f: ProductFilters): boolean {
  return f.query.trim() !== '' || Object.keys(f.attrs).length > 0;
}

/** All searchable text of a product (its own columns + legacy fields). */
function haystack(p: Product): string {
  const parts: Array<string | undefined> = [
    p.modelNumber, p.partNo, p.processor, p.ram, p.series, p.storage, p.graphics,
  ];
  if (p.attrs) for (const a of p.attrs) parts.push(a.v);
  return parts.filter(Boolean).join(' ').toLowerCase();
}

/** Value of one column for a product (attrs first, legacy field fallback). */
function columnValue(p: Product, column: string): string {
  const hit = p.attrs?.find((a) => a.k === column);
  if (hit) return hit.v;
  const legacy: Record<string, string | undefined> = {
    Series: p.series,
    Model: p.modelNumber,
    'Part No': p.partNo,
    Processor: p.processor,
    RAM: p.ram,
    Storage: p.storage,
    Graphics: p.graphics,
  };
  return legacy[column] ?? '';
}

export function applyFilters(products: Product[], f: ProductFilters): Product[] {
  const needle = f.query.trim().toLowerCase();
  const pairs = Object.entries(f.attrs);
  return products.filter((p) => {
    if (needle && !haystack(p).includes(needle)) return false;
    for (const [column, value] of pairs) {
      if (columnValue(p, column) !== value) return false;
    }
    return true;
  });
}

/** One filterable column of the active sheet. */
export interface FilterFacet {
  /** The sheet's own column name (what the dropdown is labelled with). */
  name: string;
  /** Short label shown to dealers, e.g. "Series". */
  label: string;
  values: string[];
}

/**
 * The ONLY four filters dealers get, in this order. Each is matched against the
 * active sheet's real column names (sheets spell them differently: "MODEL
 * NUMBER" vs "Model#", "PART NO." vs "Part #"), so the same four filters work
 * on every tab.
 */
interface FilterKind { label: string; match: RegExp }

const FILTER_KINDS: FilterKind[] = [
  { label: 'Series', match: /^SERIES$/ },
  { label: 'Model', match: /MODEL/ },
  { label: 'Part No', match: /PART|^P\/N$|^PN$/ },
  { label: 'Processor', match: /PROCESSOR|^CPU$/ },
];

/**
 * LAPTOP tabs swap "Part No" for "Graphics": dealers filter laptops by GPU far
 * more than by part number (owner request, Aug 2026). Every other category
 * keeps the standard four. Matched on the category NAME, so it holds for
 * "LAPTOP", "LAPTOPS" and any future laptop sheet.
 */
const LAPTOP_CATEGORY = /laptop/i;
function filterKindsFor(category: string | null | undefined): FilterKind[] {
  if (category && LAPTOP_CATEGORY.test(category)) {
    return FILTER_KINDS.map((k) =>
      k.label === 'Part No' ? { label: 'Graphics', match: /GRAPHIC|^GPU$/ } : k,
    );
  }
  return FILTER_KINDS;
}

function normalize(name: string): string {
  return name.replace(/\s+/g, ' ').trim().toUpperCase().replace(/[.\s]+$/g, '');
}

/** The active sheet's column names, in sheet order. */
function columnsOf(products: Product[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const p of products) {
    for (const a of p.attrs ?? []) {
      if (!seen.has(a.k)) {
        seen.add(a.k);
        order.push(a.k);
      }
    }
  }
  if (order.length === 0) return ['Series', 'Model', 'Part No', 'Processor', 'Graphics'];
  return order;
}

/**
 * Build the four filter dropdowns for the CURRENT category, CASCADING: each
 * dropdown lists only values that still exist under the *other* active
 * selections. So picking a series narrows the model list to that series.
 */
export function buildFacets(
  products: Product[],
  filters: ProductFilters = emptyFilters,
  category?: string | null,
): FilterFacet[] {
  if (products.length === 0) return [];
  const columns = columnsOf(products);

  const facets: FilterFacet[] = [];
  const used = new Set<string>();
  for (const kind of filterKindsFor(category)) {
    const column = columns.find((c) => !used.has(c) && kind.match.test(normalize(c)));
    if (!column) continue;
    used.add(column);

    // Narrow by every OTHER selection (not this column's own), so the dealer can
    // still change this filter without clearing it first.
    const others: ProductFilters = {
      query: filters.query,
      attrs: Object.fromEntries(
        Object.entries(filters.attrs).filter(([k]) => k !== column),
      ),
    };
    const pool = applyFilters(products, others);

    const values = new Set<string>();
    for (const p of pool) {
      const v = columnValue(p, column);
      if (v) values.add(v);
    }
    if (values.size > 0) {
      facets.push({
        name: column,
        label: kind.label,
        values: Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      });
    }
  }
  return facets;
}

/**
 * Drop selections that no longer exist under the current filters (e.g. the
 * chosen model isn't in the newly chosen series). Returns the same object when
 * nothing changed, so it's safe to call on every render.
 */
export function pruneFilters(
  products: Product[],
  filters: ProductFilters,
  category?: string | null,
): ProductFilters {
  if (Object.keys(filters.attrs).length === 0) return filters;
  const facets = buildFacets(products, filters, category);
  const kept: Record<string, string> = {};
  for (const [column, value] of Object.entries(filters.attrs)) {
    const facet = facets.find((f) => f.name === column);
    if (facet && facet.values.includes(value)) kept[column] = value;
  }
  const changed = Object.keys(kept).length !== Object.keys(filters.attrs).length;
  return changed ? { ...filters, attrs: kept } : filters;
}

/**
 * Group products by sectionTitle for section headers (feature #4), preserving
 * the incoming (sheet) order within each group and first-seen group order.
 */
export interface ProductSection {
  title: string | null;
  products: Product[];
}

export function groupBySection(products: Product[]): ProductSection[] {
  const order: (string | null)[] = [];
  const map = new Map<string | null, Product[]>();
  for (const p of products) {
    const key = p.sectionTitle ?? null;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(p);
  }
  return order.map((title) => ({ title, products: map.get(title)! }));
}
